import { useState, useEffect, useRef } from "react";
import { useGlobalContext } from "../../Config";
import { Panel } from "primereact/panel";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import {
  getFiltrosReporteCotizacion,
  getReporteCotizacion,
  getReporteNotaVenta,
  getReporteBloqueados,
  getReporteTiempoCotizacion,
  getReporteDetalleNoConfirmado,
  exportarReporteCotizacion,
  exportarReporteNotaVenta,
  exportarReporteBloqueados,
  exportarReporteTiempoCotizacion,
  exportarReporteDetalleNoConfirmado,
} from "../../service/AffariService.js";
import Filtros from "./components/Filtros.js";
import Tabla from "./components/Tabla.js";
import LoaderTransparent from "../../components/ui/LoaderTransparent.js";

const defaultFilters = {
  fechaDesde: null,
  fechaHasta: null,
  folioDesde: "",
  folioHasta: "",
  clienteCodigo: "",
  clienteRut: "",
  razonSocial: "",
  oficina: null,
  listaPrecios: null,
  vendedor: null,
  canal: null,
  origen: null,
  estado: null,
  visualizacion: "ultimas",
  historia: false,
  tipodetipoentrega: "todos",
  codigoproducto: "",
  descripcionproducto: "",
  notaVentaDesde: "",
  notaVentaHasta: "",
};

const formatDateForApi = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const appendArrayParams = (params, key, items) => {
  if (!Array.isArray(items)) return;

  items.forEach((item) => {
    let value = item;

    if (item && typeof item === "object") {
      if (item.codigo !== undefined) {
        value = item.codigo;
      } else if (item.id !== undefined) {
        value = item.id;
      } else if (item.value !== undefined) {
        value = item.value;
      } else if (item.descripcion !== undefined) {
        value = item.descripcion;
      }
    }

    if (value !== undefined && value !== null && value !== "") {
      params.append(`${key}[]`, value);
    }
  });
};

const buildReporteParams = (currentFilters, activeVariant) => {
  const params = new URLSearchParams();

  if (currentFilters.folioDesde) {
    params.append("folioDesde", currentFilters.folioDesde);
  }

  if (currentFilters.folioHasta) {
    params.append("folioHasta", currentFilters.folioHasta);
  }

  if (currentFilters.clienteRut) {
    params.append("rut", currentFilters.clienteRut);
  }

  appendArrayParams(params, "zona", currentFilters.oficina);
  appendArrayParams(params, "listaPrecio", currentFilters.listaPrecios);
  appendArrayParams(params, "vendedor", currentFilters.vendedor);

  if (activeVariant === "COTIZACION") {
    if (currentFilters.clienteCodigo) {
      params.append("codigo", currentFilters.clienteCodigo);
    }

    appendArrayParams(params, "canal", currentFilters.canal);
    appendArrayParams(params, "origen", currentFilters.origen);

    if (Array.isArray(currentFilters.estado)) {
      appendArrayParams(params, "estado", currentFilters.estado);
    }

    if (currentFilters.codigoproducto) {
      params.append("producto[]", currentFilters.codigoproducto);
    }

    const ordenMap = { todas: "T", primeras: "P", ultimas: "U" };
    params.append("orden", ordenMap[currentFilters.visualizacion] || "U");
    params.append("historia", currentFilters.historia ? "true" : "false");
    params.append("tipo", activeVariant);
  }

  if (activeVariant === "NOTAVENTA" || activeVariant === "BLOQUEADA") {
    if (currentFilters.clienteCodigo) {
      params.append("cliente", currentFilters.clienteCodigo);
    }

    if (currentFilters.razonSocial) {
      params.append("razon_social", currentFilters.razonSocial);
    }

    if (currentFilters.codigoproducto) {
      params.append("productoDesde", currentFilters.codigoproducto);
      params.append("productoHasta", currentFilters.codigoproducto);
    }
  }

  if (activeVariant === "NOTAVENTA") {
    if (currentFilters.notaVentaDesde) {
      params.append("ordenVentaDesde", currentFilters.notaVentaDesde);
    }

    if (currentFilters.notaVentaHasta) {
      params.append("ordenVentaHasta", currentFilters.notaVentaHasta);
    }

    const estadoNotaVentaMap = {
      todos: 0,
      confirmados: 1,
      pendientes: 2,
    };

    const condicionMap = {
      todos: 0,
      retiro: 1,
      despacho: 2,
    };

    params.append("estado", estadoNotaVentaMap[currentFilters.estado] ?? 0);
    params.append(
      "condicion",
      condicionMap[currentFilters.tipodetipoentrega] ?? 0,
    );
  }

  if (activeVariant === "BLOQUEADA") {
    params.append("estado", "0");
  }

  if (currentFilters.fechaDesde) {
    params.append("fechaDesde", formatDateForApi(currentFilters.fechaDesde));
  }

  if (currentFilters.fechaHasta) {
    params.append("fechaHasta", formatDateForApi(currentFilters.fechaHasta));
  }

  return params;
};

const ReportePage = ({ titulo, variant, unificadoDinar = false }) => {
  const { isDinar } = useGlobalContext();
  const [selectedVariant, setSelectedVariant] = useState(
    variant || "COTIZACION",
  );
  const activeVariant = unificadoDinar && isDinar ? selectedVariant : variant;
  const tituloReporte = unificadoDinar && isDinar ? "Reportes" : titulo;
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [causas, setCausas] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [estados, setEstados] = useState([]);
  const [glosacausas, setGlosacausas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [collapsedFiltro, setCollapsedFiltro] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const toast = useRef(null);

  useEffect(() => {
    getFiltrosReporteCotizacion().then((data) => {
      setFilterOptions(data);

      if (data) {
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          const [day, month, year] = dateStr.split("/");
          return new Date(year, month - 1, day);
        };

        setFilters((prev) => ({
          ...prev,
          fechaDesde: data.fechaDesde
            ? parseDate(data.fechaDesde)
            : prev.fechaDesde,
          fechaHasta: data.fechaHasta
            ? parseDate(data.fechaHasta)
            : prev.fechaHasta,
        }));
      }
    });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleVariantChange = (nextVariant) => {
    setSelectedVariant(nextVariant);

    setFilters((prev) => ({
      ...prev,
      estado: nextVariant === "NOTAVENTA" ? "todos" : null,
      tipodetipoentrega: "todos",
      folioDesde: "",
      folioHasta: "",
      notaVentaDesde: "",
      notaVentaHasta: "",
    }));

    setCotizaciones([]);
    setCausas([]);
    setCompetencias([]);
    setEstados([]);
    setGlosacausas([]);
  };

  const handleSearch = async (currentFilters) => {
    setLoading(true);
    const queryString = buildReporteParams(
      currentFilters,
      activeVariant,
    ).toString();

    try {
      let response;

      if (activeVariant === "NOTAVENTA") {
        response = await getReporteNotaVenta(queryString);
      } else if (activeVariant === "BLOQUEADA") {
        response = await getReporteBloqueados(queryString);
      } else if (activeVariant === "TIEMPO") {
        response = await getReporteTiempoCotizacion(queryString);
      } else if (activeVariant === "NOCONFIRMADOS") {
        response = await getReporteDetalleNoConfirmado(queryString);
      } else {
        response = await getReporteCotizacion(queryString);
      }

      setCausas(response?.causas || []);
      setCompetencias(response?.competencias || []);
      setCotizaciones(response?.cotizaciones || []);
      setEstados(response?.estados || []);
      setGlosacausas(response?.glosaCausas || {});

      setCollapsedFiltro(true);
    } catch (error) {
      console.error("Error fetching reporte:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Error al obtener el reporte",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (currentFilters) => {
    setExporting(true);

    try {
      const params = buildReporteParams(currentFilters, activeVariant);
      let mensaje = "El reporte de cotizaciones se descargó correctamente.";

      if (activeVariant === "NOTAVENTA") {
        await exportarReporteNotaVenta(params);
        mensaje = "El reporte de notas de venta se descargó correctamente.";
      } else if (activeVariant === "BLOQUEADA") {
        await exportarReporteBloqueados(params);
        mensaje =
          "El reporte de cotizaciones bloqueadas se descargó correctamente.";
      } else if (activeVariant === "TIEMPO") {
        await exportarReporteTiempoCotizacion(params);
        mensaje =
          "El reporte de tiempo de respuesta se descargó correctamente.";
      } else if (activeVariant === "NOCONFIRMADOS") {
        await exportarReporteDetalleNoConfirmado(params);
        mensaje =
          "El reporte de detalles no confirmados se descargó correctamente.";
      } else {
        await exportarReporteCotizacion(params);
      }

      toast.current?.show({
        severity: "success",
        summary: "Exportación generada",
        detail: mensaje,
      });
    } catch (error) {
      console.error("Error exportando reporte:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No fue posible exportar el reporte seleccionado.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-2">
      <Toast
        ref={toast}
        position="top-right"
        appendTo={document.body}
        baseZIndex={4000}
      />

      <div
        className={`flex flex-column ${isMobile ? "" : "overflow-hidden"}`}
        style={isMobile ? {} : { height: "calc(100vh - 6rem)" }}
      >
        <div className="p-fluid flex-none">
          <Card className="bg-blue-50 border-bottom-1 border-blue-200 py-2 px-3 mb-2">
            <h3 className="m-0 text-blue-700">{tituloReporte}</h3>
          </Card>

          <Panel
            header={
              <div
                className="flex align-items-center cursor-pointer"
                onClick={() => setCollapsedFiltro((prev) => !prev)}
              >
                <div className="surface-100 border-circle w-2rem h-2rem flex align-items-center justify-content-center mr-3">
                  <i className="pi pi-list text-primary text-sm" />
                </div>
                <span className="text-xl text-900">Filtros</span>
              </div>
            }
            toggleable
            collapsed={collapsedFiltro}
            onToggle={(e) => setCollapsedFiltro(e.value)}
            className="border-none shadow-1 border-round-xl overflow-hidden surface-card"
          >
            <div className="p-2">
              <Filtros
                data={filterOptions}
                onSearch={handleSearch}
                onExport={handleExport}
                loading={loading}
                exporting={exporting}
                filters={filters}
                setFilters={setFilters}
                variant={activeVariant}
                unificadoDinar={unificadoDinar && isDinar}
                selectedVariant={activeVariant}
                onVariantChange={handleVariantChange}
              />
            </div>
          </Panel>
        </div>

        <div
          className={`p-fluid mt-2 ${isMobile ? "" : "flex-1 overflow-hidden"}`}
        >
          <Panel className="border-none shadow-1 border-round-xl overflow-hidden surface-card panel-full-height">
            <div
              className="h-full flex flex-column p-2"
              style={{ position: "relative" }}
            >
              {loading && (
                <LoaderTransparent coverParent overlayTint="transparent" />
              )}
              <Tabla
                setLoading={setLoading}
                cotizaciones={cotizaciones}
                estados={estados}
                glosacausas={glosacausas}
                competencias={competencias}
                causas={causas}
                toast={toast}
                onRefresh={() => handleSearch(filters)}
                variant={activeVariant}
              />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default ReportePage;
